import type {
  ProposalActivity,
  ProposalCollaborationEnvelope,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import { proposalFieldForTimelinePath } from "work/proposals/negotiation/proposal-field-timeline-path";
import { proposalContentActivityKinds } from "work/proposals/negotiation/proposal-field-unread";
import type ProposalField from "work/proposals/proposal-fields/proposal-field";

/**
 * The two facts an incoming change is derived from: where the negotiation stream now stands, and the
 * content changes it recently carried. Nothing else about the envelope matters here, so a caller can
 * ask this question with a projection of it.
 */
export type ProposalIncomingChangeEnvelope = Pick<ProposalCollaborationEnvelope, "streamVersion" | "recentActivity">;

/**
 * The stream sequence of the most recent content change to `field` that this viewer did not author —
 * 0 when the field has none.
 *
 * "Incoming" is decided by authorship, not by transport. A realtime notice only tells the browser the
 * stream moved; what it then refetches is the canonical envelope, so a change that arrives by push and
 * the same change seen after a refresh are the same fact and must highlight identically. The viewer's
 * own edits are excluded twice over: an unsaved edit lives only in the local queue and never reaches
 * `recentActivity` at all, and once saved it comes back authored by them. A teammate or reviewer on the
 * viewer's own side does count — a value moving under you is worth noticing whichever side moved it,
 * which is the same rule {@link proposalFieldUnread} applies to the change badge.
 *
 * Membership is decided by resolving the activity's path back to its field rather than by comparing
 * path strings, because a collection-rooted field's changes land on descendant paths
 * ("/feeSchedule/categories" for Fee Schedule).
 */
export function latestIncomingProposalFieldChangeSequence(
  envelope: ProposalIncomingChangeEnvelope | undefined,
  field: ProposalField | null | undefined,
  currentUserId: string | undefined,
): number {
  if (!envelope || !field) return 0;

  let latestSequence = 0;
  for (const activity of envelope.recentActivity) {
    if (!isIncomingContentChangeToField(activity, field, currentUserId)) continue;
    if (activity.sequence > latestSequence) latestSequence = activity.sequence;
  }
  return latestSequence;
}

function isIncomingContentChangeToField(
  activity: ProposalActivity,
  field: ProposalField,
  currentUserId: string | undefined,
): boolean {
  if (!activity.path) return false;
  if (!proposalContentActivityKinds.has(activity.kind)) return false;
  if (activity.actorUserId === currentUserId) return false;
  const activityField = proposalFieldForTimelinePath(activity.path);
  return activityField !== undefined && activityField.isEqualTo(field);
}
